/**
 * «Register» under Medlemmer: the list the office works from.  Members register «pro» (meeting notes 2026-10-31, spec 04).
 *
 * Before: a name search and a status, four columns, and rows that only opened a person. Now: filters (status, age band,
 * gender, a tag, joined this year, has the app, household — and «betalt i år» for those who may see gifts), five sorts,
 * a dense table (nr, navn, alder, kjønn, status, innmeldt, app, gitt i år, stikkord), and a SELECTION: tick people (or
 * «Velg alle treff») and then «Send varsel til utvalget», «Eksporter utvalget» or «Kontaktliste».
 *
 * The rules are core's (member-filters) and the server's: money columns, the money filter and the money sorts are not
 * offered without donations.read (the server refuses them too); phone and e-mail appear only in the file and the
 * contact list, and only for those the server allows (membership.decide). A push to a selection is a DRAFT that someone
 * else approves under Varsler — nothing goes out from here. Every result is a toast.
 */
import React, { useEffect, useState } from 'react';
import { API, can, errText, getSession, post, statusText, saveBlob, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { kr, longDate, osloDate } from '../lib/format';
import { useNav } from '../lib/nav';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Tag } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { StandingTag } from '../components/people';
import { fromCard } from '../lib/membership-status';
import { personHref } from './Register';
import { EMPTY_FILTERS, queryOf, type Filters } from '../lib/member-register';

const GENDER_NO: Record<string, string> = { female: 'Kvinne', male: 'Mann' };
const YES_NO = [['', 'Alle'], ['yes', 'Ja'], ['no', 'Nei']] as const;
const SORTS: [string, string, boolean][] = [['name', 'Navn', false], ['memberNo', 'Medlemsnummer', false], ['joined', 'Innmeldt', false],
  ['lastGift', 'Sist gave', true], ['givenThisYear', 'Gitt i år', true]];
const EMPTY = EMPTY_FILTERS;
const PUSH_TITLE_MAX = 65, PUSH_BODY_MAX = 178;
export { queryOf };

/** A file the server makes from a POST body (a selection is too long for an address), saved like download(). */
async function postDownload(path: string, body: unknown, filename: string): Promise<{ ok: boolean; message?: string }> {
  const s = getSession();
  try {
    const r = await fetch(API + '/admin' + path, { method: 'POST', body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json', ...(s ? { Authorization: 'Bearer ' + s.token } : {}) } });
    if (!r.ok) { const b = await r.json().catch(() => ({})); return { ok: false, message: (b && (b.message_no || b.message)) || statusText(r.status) }; }
    saveBlob(await r.blob(), filename);
    return { ok: true };
  } catch { return { ok: false, message: 'Får ikke kontakt med serveren.' }; }
}

export function MemberRegister({ importedAt }: { importedAt?: string }) {
  const { caps, today } = useNav();
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [f, setF] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pushing, setPushing] = useState(false);
  const [contacts, setContacts] = useState(false);
  const [, say] = useMsg();
  const qs = queryOf(search, f, page);
  const { data, error, reload } = useLoad<Body>(`/members/list?${qs}`, [importedAt]);
  const set = (k: keyof Filters, v: string) => { setF({ ...f, [k]: v }); setPage(0); };
  const money = Boolean(data && data.can && data.can.money);
  const items: Body[] = (data && data.items) || [];
  const max: number = (data && data.maxSelection) || 500;
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else if (n.size < max) n.add(id); return n; });
  const pageAll = items.length > 0 && items.every((m) => picked.has(m.id));
  const togglePage = () => setPicked((s) => { const n = new Set(s); for (const m of items) { if (pageAll) n.delete(m.id); else if (n.size < max) n.add(m.id); } return n; });
  const ids = [...picked];
  const exportAll = async () => {
    const r = await postDownload('/members/export.csv', { ...Object.fromEntries(new URLSearchParams(queryOf(search, f))) }, `medlemmer-${today || osloDate(new Date())}.csv`);
    say(r.ok ? 'Filen er lastet ned. Eksporten står i aktivitetsloggen.' : r.message || 'Kunne ikke lage filen.', r.ok ? 'ok' : 'err');
  };
  const exportPicked = async () => {
    const r = await postDownload('/members/export.csv', { ids }, `medlemmer-utvalg-${today || osloDate(new Date())}.csv`);
    say(r.ok ? `Utvalget (${ids.length}) er lastet ned. Eksporten står i aktivitetsloggen.` : r.message || 'Kunne ikke lage filen.', r.ok ? 'ok' : 'err');
  };
  if (contacts) return <ContactList ids={ids} logOn={Boolean(caps && caps.includes('contact_log')) && can('members.register')} onBack={() => setContacts(false)} />;
  return <Card title="Medlemsregister" hint="Filtrer, velg personer og send dem et varsel, lag en kontaktliste eller eksporter. For personer fra importen kommer statusen fra medlemsregisteret.">
    <CardBody>
      <form role="search" onSubmit={(e) => { e.preventDefault(); setPage(0); setSearch(q.trim()); }} className="mreg-filters">
        <Field label="Navn eller medlemsnummer" style={{ flex: '1 1 220px', margin: 0 }}>{(id) => <input id={id} value={q} onChange={(e) => setQ(e.target.value)} />}</Field>
        <Field label="Status" style={{ margin: 0 }}>{(id) => <select id={id} value={f.status} onChange={(e) => set('status', e.target.value)}>
          <option value="all">Alle</option><option value="active">Medlem</option><option value="other">Ikke medlem nå</option></select>}</Field>
        <Field label="Alder" style={{ margin: 0 }}>{(id) => <select id={id} value={f.age} onChange={(e) => set('age', e.target.value)}>
          <option value="">Alle</option>{((data && data.ageBands) || []).map((b: Body) => <option key={b.key} value={b.key}>{b.label_no}</option>)}</select>}</Field>
        <Field label="Kjønn" style={{ margin: 0 }}>{(id) => <select id={id} value={f.gender} onChange={(e) => set('gender', e.target.value)}>
          <option value="">Alle</option><option value="female">Kvinne</option><option value="male">Mann</option><option value="unknown">Ikke oppgitt</option></select>}</Field>
        <Field label="Stikkord" style={{ margin: 0 }}>{(id) => <select id={id} value={f.tag} onChange={(e) => set('tag', e.target.value)}>
          <option value="">Alle</option>{((data && data.tags) || []).map((t: string) => <option key={t} value={t}>{t}</option>)}</select>}</Field>
        {money && <Field label="Betalt i år" style={{ margin: 0 }}>{(id) => <select id={id} value={f.paid} onChange={(e) => set('paid', e.target.value)}>
          {YES_NO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>}</Field>}
        <Field label="Meldt inn i år" style={{ margin: 0 }}>{(id) => <select id={id} value={f.joined} onChange={(e) => set('joined', e.target.value)}>
          {YES_NO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>}</Field>
        <Field label="Har appen" style={{ margin: 0 }}>{(id) => <select id={id} value={f.app} onChange={(e) => set('app', e.target.value)}>
          {YES_NO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>}</Field>
        <Field label="Husstand" style={{ margin: 0 }}>{(id) => <select id={id} value={f.household} onChange={(e) => set('household', e.target.value)}>
          {YES_NO.map(([v, l]) => <option key={v} value={v}>{l === 'Ja' ? 'Har foresatt/barn' : l === 'Nei' ? 'Ingen' : l}</option>)}</select>}</Field>
        <Field label="Sorter etter" style={{ margin: 0 }}>{(id) => <select id={id} value={f.sort} onChange={(e) => { setF({ ...f, sort: e.target.value, dir: '' }); setPage(0); }}>
          {SORTS.filter(([, , m]) => !m || money).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>}</Field>
        <Field label="Rekkefølge" style={{ margin: 0 }}>{(id) => <select id={id} value={f.dir} onChange={(e) => set('dir', e.target.value)}>
          <option value="">Vanlig</option><option value="asc">Stigende</option><option value="desc">Synkende</option></select>}</Field>
        <div className="acts" style={{ margin: 0 }}><Btn type="submit">Søk</Btn>
          <Btn ghost type="button" onClick={() => { setQ(''); setSearch(''); setF(EMPTY); setPage(0); }}>Nullstill</Btn></div>
      </form>
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : <>
        <div className="mreg-sel" role="group" aria-label="Utvalg">
          <span className="note">{data.total} treff · {data.activeCount} medlemmer i søket{picked.size ? ` · ${picked.size} valgt (utvalget beholdes når du endrer filteret)` : ''}</span>
          <span className="sp" />
          {data.ids && data.total > 0 && <Btn ghost onClick={() => setPicked(new Set(data.ids))}>Velg alle {data.total} treff</Btn>}
          {picked.size > 0 && <>
            {can('push.compose') && <Btn onClick={() => setPushing(true)}>Send varsel til utvalget</Btn>}
            <Btn ghost onClick={exportPicked}>Eksporter utvalget</Btn>
            <Btn ghost onClick={() => setContacts(true)}>Kontaktliste</Btn>
            <Btn ghost onClick={() => setPicked(new Set())}>Tøm utvalget</Btn>
          </>}
          {picked.size === 0 && <Btn ghost onClick={exportAll}>Eksporter alle treff (CSV)</Btn>}
        </div>
        <div style={{ overflowX: 'auto' }}><table className="t mreg-t">
          <thead><tr>
            <th scope="col"><input type="checkbox" aria-label="Velg alle på siden" checked={pageAll} onChange={togglePage} /></th>
            <th scope="col">Nr.</th><th scope="col">Navn</th><th scope="col">Alder</th><th scope="col">Kjønn</th><th scope="col">Status</th>
            <th scope="col">Innmeldt</th><th scope="col">App</th>{money && <th scope="col" className="num">Gitt i år</th>}<th scope="col">Stikkord</th>
          </tr></thead>
          <tbody>{items.map((m) => <tr key={m.id}>
            <td><input type="checkbox" aria-label={`Velg ${m.name}`} checked={picked.has(m.id)} onChange={() => toggle(m.id)} /></td>
            <td>{m.memberNo || m.solidusNo}</td>
            <td dir="auto"><a href={personHref(m.id)}>{m.name}</a></td>
            <td>{m.age ?? '—'}</td>
            <td>{GENDER_NO[m.gender] || '—'}</td>
            <td><StandingTag s={fromCard(m.state, m.status)} /></td>
            <td>{m.joinedOn ? longDate(m.joinedOn) : '—'}</td>
            <td>{m.hasApp ? <Tag tone="green">app</Tag> : '—'}</td>
            {money && <td className="num">{m.givenThisYear ? kr(m.givenThisYear) : '—'}</td>}
            <td>{(m.tags || []).map((t: string) => <Tag key={t} tone="grey">{t}</Tag>)}</td>
          </tr>)}</tbody></table></div>
        {!items.length && <p>Ingen personer funnet.</p>}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
          <Btn ghost disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Forrige</Btn>
          <span>Side {page + 1} av {Math.max(1, Math.ceil(data.total / 50))}</span>
          <Btn ghost disabled={(page + 1) * 50 >= data.total} onClick={() => setPage((p) => p + 1)}>Neste</Btn>
        </div>
      </>}
    </CardBody>
    <PushDialog open={pushing} ids={ids} onClose={() => setPushing(false)} onSent={() => { setPushing(false); }} />
  </Card>;
}

/**
 * «Send varsel til utvalget»: the push composer's two fields, addressed to the selection. A DRAFT for someone else to
 * approve under Varsler; it reaches only the people in the selection whose own app account is linked to them.
 */
function PushDialog({ open, ids, onClose, onSent }: { open: boolean; ids: string[]; onClose: () => void; onSent: () => void }) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [, say] = useMsg();
  const who = `Utvalg: ${ids.length} ${ids.length === 1 ? 'medlem' : 'medlemmer'}`;
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    const r = await post('/push', { kind: 'notice', title_no: title.trim(), body_no: text.trim(), audience: { memberIds: ids } }).finally(() => setBusy(false));
    say(r.ok ? 'Sendt til godkjenning. Ingenting går ut før en annen har godkjent det under Varsler.' : errText(r.body), r.ok ? 'ok' : 'err');
    if (r.ok) { setTitle(''); setText(''); onSent(); }
  };
  return <FormDialog open={open} title="Send varsel til utvalget" dirty={Boolean(title || text)} onClose={onClose}
    hint={`${who}. Bare de som har appen og er koblet til sitt eget medlemskap, får varselet.`}>
    <Field label="Tittel" required note={`Maks ${PUSH_TITLE_MAX} tegn — lengre titler kuttes på låseskjermen.`}>{(id, d) =>
      <input id={id} aria-describedby={d} maxLength={PUSH_TITLE_MAX} value={title} onChange={(e) => setTitle(e.target.value)} />}</Field>
    <Field label="Tekst" required note={`Maks ${PUSH_BODY_MAX} tegn.`}>{(id, d) =>
      <textarea id={id} aria-describedby={d} rows={3} maxLength={PUSH_BODY_MAX} value={text} onChange={(e) => setText(e.target.value)} />}</Field>
    <p className="note">Mottakere: {who}. Varselet sendes til godkjenning; en annen ansatt godkjenner det under Varsler.</p>
    <div className="acts"><Btn disabled={busy || !title.trim() || !text.trim()} aria-busy={busy || undefined} onClick={submit}>{busy ? 'Sender …' : 'Send til godkjenning'}</Btn>
      <Btn ghost onClick={onClose}>Avbryt</Btn></div>
  </FormDialog>;
}

/**
 * «Kontaktliste»: the selection, with phone numbers for those who may see them, to go through and call — print it, take
 * it as a file, and log each call or visit as you go (only with «Kontaktlogg» on and members.register).
 */
function ContactList({ ids, logOn, onBack }: { ids: string[]; logOn: boolean; onBack: () => void }) {
  const [data, setData] = useState<Body | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [done, setDone] = useState<Record<string, string>>({});
  const [, say] = useMsg();
  const { today } = useNav();
  const key = ids.join();
  useEffect(() => {
    let live = true;
    void post('/members/contact-list', { ids }).then((r) => { if (live) { if (r.ok) setData(r.body); else setFailed(errText(r.body)); } });
    return () => { live = false; };
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
  const log = async (m: Body, channel: 'phone' | 'visit') => {
    const r = await post('/touches', { kind: 'member', id: m.id, channel });
    if (r.ok) { setDone((d) => ({ ...d, [m.id]: channel === 'phone' ? 'Ringt' : 'Besøkt' })); say(`Loggført: ${channel === 'phone' ? 'ringte' : 'besøkte'} ${m.name}.`, 'ok'); }
    else say(errText(r.body), 'err');
  };
  const file = async () => {
    const r = await postDownload('/members/export.csv', { ids }, `kontaktliste-${today || osloDate(new Date())}.csv`);
    say(r.ok ? 'Kontaktlisten er lastet ned. Eksporten står i aktivitetsloggen.' : r.message || 'Kunne ikke lage filen.', r.ok ? 'ok' : 'err');
  };
  return <Card title={`Kontaktliste · ${ids.length}`} hint="Gå gjennom listen og ta kontakt. Skriv den ut, eller ta den med som fil." className="mreg-contacts">
    <CardBody>
      <div className="acts no-print"><Btn ghost onClick={onBack}>← Til registeret</Btn><span className="sp" />
        <Btn ghost onClick={() => window.print()}>Skriv ut</Btn><Btn ghost onClick={file}>Last ned (CSV)</Btn></div>
      {failed ? <LoadError error={failed} /> : !data ? <Loading /> : <>
        {!data.phones && <p className="note">Telefonnumre vises bare for dem som kan se dem i registeret.</p>}
        <table className="t"><thead><tr><th scope="col">Navn</th><th scope="col">Nr.</th><th scope="col">Alder</th>{data.phones && <th scope="col">Telefon</th>}
          {logOn && <th scope="col" className="no-print"><span className="vh">Logg kontakt</span></th>}</tr></thead>
          <tbody>{(data.items || []).map((m: Body) => <tr key={m.id}>
            <td dir="auto"><a href={personHref(m.id)}>{m.name}</a></td><td>{m.memberNo}</td><td>{m.age ?? '—'}</td>
            {data.phones && <td>{m.phone ? <a href={`tel:${m.phone}`}>{m.phone}</a> : '—'}</td>}
            {logOn && <td className="no-print">{done[m.id] ? <Tag tone="green">{done[m.id]}</Tag> : <span className="acts" style={{ margin: 0 }}>
              <Btn ghost aria-label={`Logg: ringte ${m.name}`} onClick={() => log(m, 'phone')}>Ringte</Btn>
              <Btn ghost aria-label={`Logg: besøkte ${m.name}`} onClick={() => log(m, 'visit')}>Besøkte</Btn></span>}</td>}
          </tr>)}</tbody></table>
      </>}
    </CardBody>
  </Card>;
}
